import { callApi } from "@src/utils/api-client";
import { getFailureTypeForHttpStatus } from "@src/utils/failure-taxonomy";
import ClientLogsService from "./client-logs.service";
import { buildInsurancePackageConfig } from "./configs/insurance-package.config";

export interface InsurancePackageBackendResponse {
  transactionID?: string;
  transactionid?: string;
  message?: string | InsurancePackageBackendPackage[];
  [key: string]: unknown;
}

export interface InsurancePackageResponse {
  transactionID?: string;
  transactionid?: string;
  message?: string | Package[];
  error?: string;
  [key: string]: unknown;
}

export interface InsurancePackageBackendPackage {
  insurancePackageName: string;
  insurancePackageId: string | number;
  enrollment_category?: EnrollmentCategory;
  payer_id?: number;
  exclusion_ind?: boolean;
  exclusion_note?: string;
  roles_allowed_to_override?: string | null;
}

export interface Package {
  name: string;
  id: string | number;
  enrollment_category?: EnrollmentCategory;
  payer_id?: number;
  exclusion_ind?: boolean;
  exclusion_note?: string;
  roles_allowed_to_override: string[];
}

export interface EnrollmentCategory {
  id: number;
  name: string | null;
}

const normalizeRolesAllowedToOverride = (value?: string | null): string[] => {
  if (!value) {
    return [];
  }

  return value
    .split(",")
    .map((role) => role.trim())
    .filter(Boolean);
};

const normalizeInsurancePackageResponse = (
  data: InsurancePackageBackendResponse | null,
): InsurancePackageResponse => {
  if (!data || typeof data !== "object") {
    throw new Error("Failed to parse insurance package information.");
  }

  const { message, ...rest } = data;

  if (!Array.isArray(message)) {
    return { ...rest, message };
  }

  return {
    ...rest,
    message: message.map((insurancePackage) => {
      const {
        insurancePackageName,
        insurancePackageId,
        roles_allowed_to_override,
        ...packageDetails
      } = insurancePackage;

      return {
        ...packageDetails,
        name: insurancePackageName,
        id: insurancePackageId,
        roles_allowed_to_override: normalizeRolesAllowedToOverride(roles_allowed_to_override),
      };
    }),
  };
};

export const fetchInsurancePackages = async (
  user_uuid: string,
  context_id: string,
  lookup_state: string,
  package_exclusions?: boolean,
): Promise<InsurancePackageResponse> => {
  try {
    const { data, response } = await callApi(
      buildInsurancePackageConfig(user_uuid, context_id, lookup_state, package_exclusions),
    );

    if (!response.ok) {
      ClientLogsService.logError(
        user_uuid,
        "insurance-package.service.fetchInsurancePackages",
        "Failed to fetch insurance package data",
        new Error(`API responded with status ${response.status}`),
        {
          errorType: getFailureTypeForHttpStatus(response.status),
          dependencyName: "InsurancePackageAPI",
          functionName: "fetchInsurancePackages",
        },
      );
      return {
        error: `Error fetching insurance package data: ${response.statusText}`,
      };
    }

    return normalizeInsurancePackageResponse(data as InsurancePackageBackendResponse);
  } catch (err: any) {
    ClientLogsService.logError(
      user_uuid,
      "insurance-package.service.fetchInsurancePackages",
      "Error fetching insurance package data",
      err instanceof Error ? err : new Error("Unknown error"),
      {
        errorType: "DependencyFailure",
        dependencyName: "InsurancePackageAPI",
        functionName: "fetchInsurancePackages",
      },
    );
    return {
      error: err.message || "Failed to fetch insurance package information.",
    };
  }
};

export default { fetchInsurancePackages };
